import { useCallback } from 'react'

import { useQuery } from '@tanstack/react-query'

import { API_URL } from '@/constants/env'

import { SweetspotError } from '@/helpers/error'

import {
  type SharePointDrive,
  type SharePointDriveListResponse,
  sharepointDrivesQueryKeys,
} from '@/hooks/sharepoint/types'
import { useAuthInfoWithThrow } from '@/hooks/user/useAuthInfoWithThrow'

import type { UUID } from '@/types/Id'

interface UseSharepointDrivesArgs<T> {
  orgId: UUID | ''
  siteId: string
  select?: (data: SharePointDriveListResponse) => T
  enabled?: boolean
}

export const fetchSharepointDrives = async (
  orgId: UUID | '',
  siteId: string,
  accessToken: string,
): Promise<SharePointDriveListResponse> => {
  if (!orgId) throw new Error('No org ID provided.')
  if (!siteId) throw new Error('No site ID provided.')
  const url = `${API_URL}/api/v1/organizations/${orgId}/sharepoint/sites/${encodeURIComponent(siteId)}/drives`
  const res = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
  })
  if (!res.ok)
    throw new SweetspotError({
      name: 'FETCH_ERROR',
      message: res.statusText,
      cause: res,
    })
  return (await res.json()) as SharePointDriveListResponse
}

export const useSharepointDrives = <T = SharePointDrive[]>({
  orgId,
  siteId,
  select,
  enabled = true,
}: UseSharepointDrivesArgs<T>) => {
  const { accessToken } = useAuthInfoWithThrow()

  const selectFn = useCallback(
    (data: SharePointDriveListResponse): T => {
      return select ? select(data) : (data.data as unknown as T)
    },
    [select],
  )

  return useQuery({
    queryKey: sharepointDrivesQueryKeys.site({ orgId, siteId }),
    queryFn: () => fetchSharepointDrives(orgId, siteId, accessToken),
    enabled: !!orgId && !!siteId && enabled,
    staleTime: Infinity,
    select: selectFn,
  })
}
